/**
 * Guided tour copy for the three roles. Each step is one sentence and names
 * a data-tour target that is rendered in the current app.
 */

const TAB_BAR_PX = 72;
const TOP_BAR_PX = 44;
const TOP_GAP_PX = 8;
const SIDE_PX = 12;
const ABOVE_TAB_GAP_PX = 12;

const AGENT_STEPS = [
  {
    title: 'Home',
    text: 'Home is your statistics: pick this week, this month, or all time, tap a tile for the breakdown, use the small Add customer and Add vendor buttons, and a banner appears only when a trial ends within 3 days or homeowners reach 80% of the limit.',
    target: 'dashboard',
    section: 'dashboard',
  },
  {
    title: 'Clients',
    text: 'Clients shows your active count in the header, and it is where you add a homeowner and open their home.',
    target: 'clients',
    section: 'clients',
  },
  {
    title: 'Vendors',
    text: 'Vendors shows Your vendors for people you added, and Team vendors when your team shares a list.',
    target: 'vendors',
    section: 'vendors',
  },
  {
    title: 'Tasks',
    text: 'Tasks shows maintenance for each home, including what needs attention.',
    target: 'tasks',
    section: 'tasks',
  },
  {
    title: 'Projects',
    text: 'Projects groups that work, and each task can use a vendor.',
    target: 'projects',
    section: 'projects',
  },
  {
    title: 'Integrations',
    text: 'Integrations sends new clients from Zapier into Pacarion.',
    target: 'integrations',
    section: 'integrations',
    webOnly: true,
  },
];

const TEAM_OWNER_STEPS = [
  AGENT_STEPS[0],
  AGENT_STEPS[1],
  AGENT_STEPS[2],
  AGENT_STEPS[3],
  AGENT_STEPS[4],
  {
    title: 'Team',
    text: 'Use Add team member to invite someone by email, up to the seats on your plan.',
    target: 'team',
    section: 'team',
  },
];

const HOMEOWNER_STEPS = [
  {
    title: 'Home',
    text: 'Home shows this house and what is coming due.',
    target: 'overview',
    section: 'overview',
  },
  {
    title: 'Search',
    text: 'Search finds a task, project, vendor, document, home detail, or place in the app.',
    target: 'home-search',
    section: 'overview',
  },
  {
    title: 'Tasks',
    text: 'Tasks is where you mark maintenance done.',
    target: 'tasks',
    section: 'tasks',
  },
  {
    title: 'Projects',
    text: 'Projects groups the bigger jobs for this home.',
    target: 'projects',
    section: 'projects',
  },
  {
    title: 'Property Details',
    text: 'Property Details opens Home facts for the walls, roof, air conditioners, water heaters, and appliances.',
    target: 'home-facts',
    section: 'property',
  },
  {
    title: 'Utility and Service Providers',
    text: 'Utility and Service Providers is the separate list of utilities and services.',
    target: 'contacts',
    section: 'contacts',
  },
  {
    title: 'Documents',
    text: 'Documents is where you keep files for this home.',
    target: 'documents',
    section: 'documents',
  },
];

function isTeamOwner(profile) {
  if (!profile) return false;
  return profile.is_team_owner === true || profile.role === 'owner';
}

function tourRole(profile) {
  return isTeamOwner(profile) ? 'teamOwner' : 'agent';
}

function reportStep() {
  return {
    title: 'Pacarion Home History Report',
    text: 'Maintenance history names each completed task, with its category and date.',
    target: 'home-history',
    section: 'settings',
  };
}

function moreStep(text) {
  return {
    title: 'More',
    text: text,
    target: 'more-rows',
    section: 'settings',
  };
}

const HOMEOWNER_MORE = 'More opens Notification Settings and Sponsor on their own screens, and Legal is a row on this page.';
const AGENT_MORE = 'More opens Billing for your plan, trial end, homeowner usage, and upgrade or add-on, and Notifications and Legal are rows on this page.';
const TEAM_MORE_WEB = 'More opens Billing for your plan, trial end, homeowner usage, and upgrade or add-on, and Notifications, Integrations, and Legal are rows on this page.';

function stepsForRole(role, options = {}) {
  const web = options.web !== false;
  const phone = options.phone === true;
  const source = role === 'homeowner'
    ? HOMEOWNER_STEPS
    : role === 'teamOwner'
      ? TEAM_OWNER_STEPS
      : AGENT_STEPS;
  const steps = source.filter((step) => web || !step.webOnly);
  if (role === 'teamOwner' && !phone && web) {
    steps.push({
      title: 'Integrations',
      text: 'Integrations sends new clients from Zapier into Pacarion.',
      target: 'integrations',
      section: 'integrations',
    });
  }
  steps.push(reportStep());
  if (role === 'homeowner') {
    steps.push(moreStep(HOMEOWNER_MORE));
  } else if (role === 'teamOwner' && phone && web) {
    steps.push(moreStep(TEAM_MORE_WEB));
  } else {
    steps.push(moreStep(AGENT_MORE));
  }
  return steps;
}

function stepsForProfile(profile, options) {
  return stepsForRole(tourRole(profile), options);
}

function tourTargetSelector(target) {
  return `[data-tour="${target}"]`;
}

/**
 * Several controls can share a data-tour id (the Team nav item and Add team
 * member). Prefer the Add team member button, including when it is disabled.
 */
function pickTourNode(nodes) {
  const list = Array.from(nodes || []);
  const addMember = list.find((item) => /add team member/i.test(item.textContent || ''));
  if (addMember) return addMember;
  const button = list.find((item) => item.tagName === 'BUTTON');
  return button || list[0] || null;
}

/**
 * Pixel box for a phone viewport. The card sits below the top bar and above
 * the 72px tab bar, including the home-indicator inset.
 */
function tourCardLayout(viewportWidth, viewportHeight, safe = {}) {
  const safeTop = Number(safe.top) || 0;
  const safeBottom = Number(safe.bottom) || 0;
  const safeLeft = Number(safe.left) || 0;
  const safeRight = Number(safe.right) || 0;
  const left = Math.max(SIDE_PX, safeLeft);
  const right = Math.max(SIDE_PX, safeRight);
  const topLimit = safeTop + TOP_BAR_PX + TOP_GAP_PX;
  const bottomOffset = TAB_BAR_PX + safeBottom + ABOVE_TAB_GAP_PX;
  const width = viewportWidth - left - right;
  const maxHeight = viewportHeight - topLimit - bottomOffset;
  return { left, right, topLimit, bottomOffset, width, maxHeight };
}

function tourCardIsOnScreen(layout, viewportWidth, viewportHeight) {
  if (!layout || layout.width <= 0 || layout.maxHeight <= 0) return false;
  const cardBottom = viewportHeight - layout.bottomOffset;
  const cardTop = cardBottom - layout.maxHeight;
  return layout.width <= viewportWidth
    && layout.left >= 0
    && layout.left + layout.width <= viewportWidth + 0.5
    && cardTop >= layout.topLimit - 0.5
    && cardBottom <= viewportHeight - TAB_BAR_PX + 0.5;
}

const TOUR_CARD_STYLE = {
  position: 'fixed',
  zIndex: 10002,
  boxSizing: 'border-box',
  left: 'max(12px, env(safe-area-inset-left, 0px))',
  right: 'max(12px, env(safe-area-inset-right, 0px))',
  width: 'auto',
  maxWidth: '420px',
  marginLeft: 'auto',
  marginRight: 'auto',
  bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 12px)',
  maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 44px - 8px - 72px - env(safe-area-inset-bottom, 0px) - 12px)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0.85rem',
  borderRadius: '1rem',
};

module.exports = {
  AGENT_STEPS,
  TEAM_OWNER_STEPS,
  HOMEOWNER_STEPS,
  TOUR_CARD_STYLE,
  isTeamOwner,
  tourRole,
  stepsForRole,
  stepsForProfile,
  tourTargetSelector,
  pickTourNode,
  tourCardLayout,
  tourCardIsOnScreen,
};
